import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { SkeletonButton, SkeletonBadge, SkeletonText, SkeletonTitle } from '@/components/ui/skeleton-base';

export default function PaymentDetailsSkeleton() {
  return (
    <div className="bg-background min-h-screen">
      <div className="flex flex-col space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-center gap-4">
            <Skeleton className="h-10 w-10" />
            <div>
              <div className="flex items-center gap-3">
                <SkeletonTitle className="w-48" />
                <SkeletonBadge />
              </div>
              <SkeletonText className="mt-1 w-40" />
            </div>
          </div>
          <div className="flex flex-col items-end gap-3">
            <SkeletonBadge className="w-32" />
            <div className="flex gap-2">
              <SkeletonButton />
              <SkeletonButton />
            </div>
          </div>
        </div>

        {/* Test Payment Warning Skeleton */}
        <div className="mt-6">
          <Skeleton className="h-16 w-full rounded-lg" />
        </div>

        {/* Main Content Grid */}
        <div className="mt-6 grid gap-6 lg:grid-cols-3">
          {/* Main content - 2/3 width */}
          <div className="space-y-6 lg:col-span-2">
            {/* Payment Overview Card */}
            <Card>
              <CardHeader className="pb-4">
                <div className="flex items-start justify-between">
                  <div className="space-y-2">
                    <SkeletonTitle className="w-64" />
                    <SkeletonText className="w-48" />
                  </div>
                  <SkeletonBadge />
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                  <div className="flex flex-col space-y-2">
                    <SkeletonText className="w-16" />
                    <SkeletonTitle className="w-32" />
                  </div>
                  <div className="flex flex-col space-y-2">
                    <SkeletonText className="w-32" />
                    <SkeletonText className="w-40" />
                    <SkeletonText className="w-36" />
                  </div>
                  <div className="flex flex-col space-y-2">
                    <SkeletonText className="w-20" />
                    <SkeletonText className="w-48" />
                    <SkeletonText className="w-32" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Payment Details Card */}
            <Card>
              <CardHeader>
                <SkeletonTitle className="w-40" />
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Basic Payment Info */}
                  <div className="grid grid-cols-2 gap-4">
                    {[...Array(6)].map((_, i) => (
                      <div key={i} className="space-y-1">
                        <SkeletonText className="w-24" />
                        <SkeletonText className="w-full" />
                      </div>
                    ))}
                  </div>

                  <Separator />

                  {/* Payment Method Details */}
                  <div className="space-y-4">
                    <SkeletonTitle className="w-48" />
                    <div className="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2">
                      {[...Array(8)].map((_, i) => (
                        <div key={i} className="space-y-2">
                          <SkeletonText className="w-24" />
                          <SkeletonText className="w-full" />
                        </div>
                      ))}
                    </div>
                  </div>

                  <Separator />

                  {/* Enrichments */}
                  <div className="space-y-4">
                    <SkeletonTitle className="w-32" />
                    <div className="space-y-4">
                      {[...Array(3)].map((_, i) => (
                        <div key={i} className="space-y-2">
                          <SkeletonText className="w-32" />
                          <Skeleton className="h-20 w-full" />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Payment Transactions Card */}
            <Card>
              <CardHeader>
                <SkeletonTitle className="w-48" />
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="rounded-lg border p-4">
                      <div className="flex items-center justify-between">
                        <div className="space-y-2">
                          <SkeletonText className="w-32" />
                          <SkeletonText className="w-48" />
                        </div>
                        <SkeletonBadge />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar - 1/3 width */}
          <div className="space-y-6">
            {/* Payment Customer Card */}
            <Card>
              <CardHeader>
                <SkeletonTitle className="w-32" />
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="space-y-2">
                      <SkeletonText className="w-24" />
                      <SkeletonText className="w-full" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
